import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, signal, inject } from '@angular/core';

import { Observable } from 'rxjs';

import { CurrentShipmentResponse, ShipmentHistoryDTO, ShipmentQueryParamInfo, ShipmentResponse } from '../models/shipments.model';
import { environment } from '../../../../environments/environment';
import { ShipmentImagesResponse } from '../models/shipment-images.model';

const httpHeaders = new HttpHeaders({ 'content-type': 'application/json' })
const DEFAULT_DISCLOSURE_OPEN_STATE: Record<string, boolean> = {};
const SUPPORTED_DISCLOSURE_KEYS = ['2', '1', '4', '7'];

@Injectable({
  providedIn: 'root',
})
export class ShipmentsDataService {
  private http = inject(HttpClient);

  apiUrl = environment.apiBaseUrl;
  wsUrl = environment.wsUrl;
  selectedShipment = signal<any>({});
  homeRefreshRequestVersion = signal(0);
  disclosureOpenState = signal<Record<string, boolean>>({ ...DEFAULT_DISCLOSURE_OPEN_STATE });
  isExtendedHeaderExpanded = signal(false);
  statementBillingEnabled = signal(false);
  isMobileImagePreviewOpen = signal(false);

  requestHomeRefresh(): void {
    this.homeRefreshRequestVersion.update((current) => current + 1);
  }

  clearSelectedShipmentState(): void {
    this.selectedShipment.set({});
    this.isExtendedHeaderExpanded.set(false);
  }

  resolveDisclosureStatusKey(statusCode: string | number | null | undefined): string | null {
    if (statusCode === null || statusCode === undefined) {
      return null;
    }

    const code = statusCode.toString();
    if (code === '0' || code === '1' || code === '6') {
      return '1';
    }

    if (code === '2' || code === '4' || code === '7') {
      return code;
    }

    return null;
  }

  setExclusiveDisclosureOpenForStatus(statusCode: string | number | null | undefined): void {
    const selectedKey = this.resolveDisclosureStatusKey(statusCode);

    if (!selectedKey) {
      return;
    }

    this.disclosureOpenState.update((current) => {
      return {
        ...current,
        [selectedKey]: true,
      };
    });
  }

  setDisclosureOpen(statusKey: string | number, isOpen: boolean): void {
    this.disclosureOpenState.update((current) => ({
      ...current,
      [statusKey.toString()]: isOpen,
    }));
  }

  resetDisclosureOpenState(): void {
    this.disclosureOpenState.set({ ...DEFAULT_DISCLOSURE_OPEN_STATE });
  }

  public getUserPermissions(): Observable<CurrentShipmentResponse> {
    return this.http.get<CurrentShipmentResponse>(this.apiUrl + '/users/GetUserPermissions', { headers: httpHeaders });
  }

  public getCurrentShipments(shipmentQuery: ShipmentQueryParamInfo): Observable<CurrentShipmentResponse> {
    return this.http.post<CurrentShipmentResponse>(this.apiUrl + '/shipments/GetCurrentShipments', shipmentQuery, { headers: httpHeaders });
  }

  public getUserAccounts(): Observable<ShipmentResponse> {
    return this.http.get<ShipmentResponse>(this.apiUrl + '/users/GetUserAccounts');
  }

  public getShipmentsStatusDetails(userAccount: number, shipmentNumber: number): Observable<ShipmentResponse> {
    return this.http.get<ShipmentResponse>(this.apiUrl + '/shipments/getShipmentsStatusDetails?userAccount=' + userAccount + '&shipmentNumber=' + shipmentNumber);
  }

  public getSingleShipmentDetails(shipmentNumber: number): Observable<{ data: ShipmentHistoryDTO; message?: string; status?: string }> {
    return this.http.get<{ data: ShipmentHistoryDTO; message?: string; status?: string }>(this.apiUrl + '/shipments/GetSingleShipmentDetails?shipmentnumber=' + shipmentNumber);
  }

  public getShipmentImages(shipmentNumber: number): Observable<ShipmentImagesResponse> {
    return this.http.get<ShipmentImagesResponse>(this.apiUrl + '/shipments/GetShipmentImages?shipmentnumber=' + shipmentNumber);
  }

  public getShipmentImageById(shipmentImageId: string): Observable<Blob> {
    return this.http.get(this.apiUrl + '/shipments/GetShipmentImage?ImageId=' + encodeURIComponent(shipmentImageId), { responseType: 'blob' });
  }

  public getShipmentFreightCharges(shipmentNumber: number, shipperAccount: number): Observable<any> {
    return this.http.get<any>(this.apiUrl + '/shipments/GetShipmentFreightCharges?shipmentNumber=' + shipmentNumber + '&debtorAccountNumber=' + shipperAccount);
  }

  public downloadShipmentImage(ImageIds: string[]): Observable<Blob> {
    const payload = {
      ImageIds: ImageIds,
    };

    return this.http.post(this.apiUrl + '/shipments/images/download', payload, { responseType: 'blob' });
  }
}
